import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraAvatar, SpecraButton, SpecraPageHeader } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronRight, Plus } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AssignedActionList, CORRECTIVE_ACTION_KIND } from '../../../components/delivery/widgets/AssignedActionList';
import { AuditList } from '../../../components/delivery/widgets/AuditList';
import { useMyAssignedActions } from '../../../components/delivery/widgets/useMyAssignedActions';
import { useTablePreferences } from '../../../hooks/useTablePreferences';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { cn } from '../../../lib/utils';
import { EntityTable } from '../_shared/components/EntityTable';
import { EmptyState } from '../_shared/components/PageStates';
import { useProgram } from '../Programs/hooks';
import { useAuditPanel } from './auditPanelStore';
import { AuditStatusBadge, auditorLabel, periodLabel } from './auditWorkflow';
import { useAudits, useStartAudit } from './hooks';
// SAD-143: the assignee view's wiring. The cap is generous because a Correction
// assignee's list is bounded by how many were raised against them on ONE program
// — Home's tighter cap exists because that surface competes for space.
const ASSIGNEE_CORRECTION_CAP = 25;
const ASSIGNEE_KINDS = [
    CORRECTIVE_ACTION_KIND
];
const ASSIGNEE_TESTIDS = {
    container: Testids.deliveryCompliance.auditAssigneeContainer,
    row: Testids.deliveryCompliance.auditAssigneeRow,
    empty: Testids.deliveryCompliance.auditAssigneeEmpty,
    error: Testids.deliveryCompliance.auditAssigneeError,
    more: Testids.deliveryCompliance.auditAssigneeMore,
    resolve: Testids.deliveryCompliance.auditAssigneeResolve
};
// A person cell: 24px initials avatar + name (§5 person recipe). An unassigned
// audit shows a plain em-dash — no empty avatar bubble.
function AssignedCell({ audit }) {
    const name = auditorLabel(audit);
    if (name === '—') return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2",
        children: [
            /*#__PURE__*/ _jsx(SpecraAvatar, {
                name: name,
                size: "sm"
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "whitespace-nowrap",
                children: name
            })
        ]
    });
}
// The list shows the score as the raw pass/total fraction (red until it clears,
// green at a perfect pass), not a percentage — the fraction is the auditor's
// working number. SAD-143: a completed self-assessment's frozen_score carries
// FIRST-PASS quality (rows that passed without needing a correction); it rides
// as a muted secondary beside the FINAL compliance fraction so an all-Pass close
// reads as the pass it is (11 / 11) while the first-pass signal stays visible. A
// published all-N/A audit falls back to its frozen string.
function ScoreCell({ audit }) {
    if (audit.totalQuestions > 0) {
        const perfect = audit.passCount === audit.totalQuestions;
        const firstPass = audit.state === 'Complete' && audit.frozenScore ? audit.frozenScore : null;
        return /*#__PURE__*/ _jsxs("span", {
            className: "tabular-nums",
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: cn('font-medium', perfect ? 'text-success' : 'text-danger'),
                    children: `${audit.passCount} / ${audit.totalQuestions}`
                }),
                firstPass ? /*#__PURE__*/ _jsx("span", {
                    className: "ml-1.5 font-normal text-muted-foreground",
                    children: `· ${firstPass} first-pass`
                }) : null
            ]
        });
    }
    if (audit.frozenScore) {
        return /*#__PURE__*/ _jsx("span", {
            className: "font-medium tabular-nums",
            children: audit.frozenScore
        });
    }
    return /*#__PURE__*/ _jsx("span", {
        className: "tabular-nums text-muted-foreground",
        children: "—"
    });
}
const columns = [
    {
        key: 'area',
        header: 'Area',
        render: (a)=>/*#__PURE__*/ _jsx("span", {
                className: "font-medium",
                children: a.area || '—'
            })
    },
    {
        key: 'period',
        header: 'Period',
        sortable: true,
        sortAccessor: (a)=>a.periodMonth || periodLabel(a),
        render: (a)=>/*#__PURE__*/ _jsx("span", {
                className: "whitespace-nowrap tabular-nums",
                children: periodLabel(a)
            })
    },
    {
        key: 'state',
        header: 'State',
        render: (a)=>/*#__PURE__*/ _jsx(AuditStatusBadge, {
                audit: a
            })
    },
    {
        key: 'assignedTo',
        header: 'Assigned To',
        sortAccessor: auditorLabel,
        render: (a)=>/*#__PURE__*/ _jsx(AssignedCell, {
                audit: a
            })
    },
    {
        key: 'score',
        header: 'Score',
        align: 'right',
        sortable: true,
        sortAccessor: (a)=>a.totalQuestions > 0 ? a.passCount * 100 / a.totalQuestions : -1,
        render: (a)=>/*#__PURE__*/ _jsx(ScoreCell, {
                audit: a
            })
    },
    {
        key: 'final',
        header: 'Final',
        render: (a)=>/*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: a.isFinal ? 'Yes' : 'No'
            })
    },
    {
        key: 'chevron',
        header: '',
        align: 'right',
        render: ()=>/*#__PURE__*/ _jsx(ChevronRight, {
                className: "ml-auto size-3.5 text-muted-foreground/50"
            })
    }
];
// SAD-143 (Matt QA round 5): what a caller outside the FULL self-assessment read
// set sees on this tab. Matt asked for the Correction assignee to reach their own
// open Correction from the program's own Audits section, not only from Program
// Home — "It should not be the full view that the program lead/quality see. It
// should just be an extremely simple page that is basically just a list of what
// you are showing in the homepage [...] the open correction, its description, and
// the ability to resolve it. That's all thats needed."
//
// Two callers land here and they must NOT get the same screen:
//
//   a caller WITH a linked contact record  -> the minimal list, resolvable inline
//   a program outsider with no linked      -> the unchanged no-access notice
//   contact record at all
//
// The no-access branch is load-bearing, not a fallback: Matt's round-3
// validation (step 7) requires an unrelated user to keep getting "a clean
// no-access state", and the correction-isolation journey asserts exactly that
// copy and testid. So the notice is preserved verbatim rather than replaced —
// F2 ADDS a lane, it does not repurpose the existing one.
//
// SAD-317: correction count is NOT an access decision, so it plays no part in
// this choice. Resolving a Correction invalidates the same assigned-actions
// query this view and its list both read, so if the notice fired on a
// zero-corrections count the whole subtree — dialog included — would unmount
// mid-resolve the instant the count dropped to zero. A linked-contact
// assignee stays on the lane and simply sees the list's own empty state.
//
// The list itself is the SAME AssignedActionList the Home "Assigned to you" group
// renders, filtered to Corrections with inline Resolve enabled, rather than a
// second implementation that could drift from it. Nothing else is added: no
// checklist, no audit list, no other assignee's rows — the server already refuses
// those to this caller, and the journey asserts their absence so this view cannot
// quietly grow into the full form.
function AssigneeCorrectionsView({ programId }) {
    const { data, isLoading, isError } = useMyAssignedActions(programId);
    // The no-access notice answers exactly one question: does the caller have a
    // linked contact record at all. Correction count is not an access decision —
    // an assignee who has just resolved their last open Correction stays on this
    // lane and sees AssignedActionList's own empty state, rather than the whole
    // subtree (and any dialog it has open) getting swapped out from underneath
    // them. Guarded to the settled case only: `data` is undefined while loading
    // or on error, and reporting "no access" then would be reporting a fact we
    // don't know yet — an in-flight or failed request is not an access decision.
    if (!isLoading && !isError && !data?.callerHasLinkedContact) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-5",
            "data-testid": Testids.deliveryCompliance.auditNoAccessNotice,
            children: [
                /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    title: "Audits",
                    description: "Program self-audits per the SDS · only non-final Program audits feed the compliance KPI"
                }),
                /*#__PURE__*/ _jsx(EmptyState, {
                    title: "No self-assessment access",
                    description: "You do not have access to self-assessments on this program. If a correction was assigned directly to you, resolve it from your Dashboard's “Assigned to you” card instead."
                })
            ]
        });
    }
    // Loading, error, and the settled assignee lane all share this same shape —
    // AssignedActionList already renders its own skeleton and its own error
    // state, so the wrapper around it never needs to change shape (and never
    // unmounts the resolve dialog it may have open) while the feed settles.
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-5",
        "data-testid": Testids.deliveryCompliance.auditAssigneeView,
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                title: "Audits",
                description: "Corrections assigned to you on this program"
            }),
            /*#__PURE__*/ _jsx(AssignedActionList, {
                programId: programId,
                cap: ASSIGNEE_CORRECTION_CAP,
                testids: ASSIGNEE_TESTIDS,
                enableResolve: true,
                onlyKinds: ASSIGNEE_KINDS,
                emptyText: "You have no open corrections on this program."
            })
        ]
    });
}
export function AuditsTab({ programId }) {
    const { canEdit } = useProgramContext();
    const openPanel = useAuditPanel((s)=>s.open);
    const { data, isLoading, error, refetch } = useAudits(programId);
    const start = useStartAudit();
    // Creating a self-assessment requires manage_self_assessment transition
    // authority (SAD-143: the Program Lead's tool, not Quality's — same
    // server-resolved gate shape as canEdit). Don't offer the flow to a caller
    // who couldn't finish it.
    const programQuery = useProgram(programId);
    const canManageSelfAssessment = programQuery.data?.canManageSelfAssessment ?? false;
    // SAD-143: server-computed capability flag (GetProgramResponse.can_view_self_assessment)
    // — the UI never re-implements policy. A caller outside the FULL read set
    // (e.g. a Correction assignee, whose own workflow lives on the Dashboard's
    // "Assigned to you" card, not this tab) now gets ListAudits/GetAudit
    // narrowed server-side to nothing; render an honest no-access state instead
    // of a table that looks like an empty program.
    const canViewSelfAssessment = programQuery.data?.canViewSelfAssessment ?? false;
    const prefs = useTablePreferences('ts:delivery:audits');
    const audits = data?.items ?? [];
    // ?audit=<id> deep-links the checklist side panel — the assigned-actions
    // feed's CORRECTIVE_ACTION destination lands here. Applied once per param
    // value (ref-guarded), only after the audit list has loaded, and only when
    // the id actually resolves in that list — a stale or foreign id is ignored
    // silently rather than crashing or opening an empty panel.
    const [searchParams] = useSearchParams();
    const auditParam = searchParams.get('audit') ?? '';
    const appliedAuditParamRef = useRef(null);
    useEffect(()=>{
        if (!auditParam) return;
        if (appliedAuditParamRef.current === auditParam) return;
        if (isLoading) return;
        appliedAuditParamRef.current = auditParam;
        if (audits.some((a)=>a.id === auditParam)) openPanel(auditParam);
    }, [
        auditParam,
        isLoading,
        audits,
        openPanel
    ]);
    // Create opens the audit side panel — the auditor stays on the program while
    // the checklist rides along. StartAudit returns the already-started audit for
    // the current month if one exists, so the button is idempotent.
    const handleCreate = async ()=>{
        try {
            const audit = await start.mutateAsync({
                programId
            });
            if (audit?.id) openPanel(audit.id);
        } catch  {
        // The start hook's onError already toasts; swallow so this click-handler
        // promise doesn't reject unhandled.
        }
    };
    if (!canViewSelfAssessment) {
        return /*#__PURE__*/ _jsx(AssigneeCorrectionsView, {
            programId: programId
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-5",
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                title: "Audits",
                description: "Program self-audits per the SDS · only non-final Program audits feed the compliance KPI",
                actions: /*#__PURE__*/ _jsxs(SpecraButton, {
                    onClick: handleCreate,
                    disabled: !canEdit || start.isPending || !canManageSelfAssessment,
                    "data-testid": Testids.deliveryCompliance.auditStartAudit,
                    children: [
                        /*#__PURE__*/ _jsx(Plus, {
                            className: "size-4"
                        }),
                        start.isPending ? 'Creating…' : 'Create Audit'
                    ]
                })
            }),
            !canManageSelfAssessment ? /*#__PURE__*/ _jsx("p", {
                className: "rounded-md border border-dashed bg-muted/40 p-3 text-xs text-muted-foreground",
                "data-testid": Testids.deliveryCompliance.auditViewOnlyNotice,
                children: "You can view self-assessments here, but only someone with self-assessment management access can create, edit, or complete them. If a correction is assigned directly to you, you can still resolve it."
            }) : null,
            /*#__PURE__*/ _jsx(AuditList, {
                programId: programId
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2.5",
                children: [
                    /*#__PURE__*/ _jsx(EntityTable, {
                        data: audits,
                        columns: columns,
                        testid: Testids.deliveryCompliance.auditsTable,
                        rowTestid: Testids.deliveryCompliance.auditRow,
                        rowKey: (a)=>a.id,
                        isLoading: isLoading,
                        error: error,
                        onRetry: refetch,
                        onRowClick: (row)=>openPanel(row.id),
                        getRowLabel: (row)=>`${row.area || 'Audit'} — ${periodLabel(row)}`,
                        sort: prefs.sort,
                        onToggleSort: prefs.toggleSort,
                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                            title: "No audits yet",
                            description: "SDS runs one full-program audit each month against the locked standard checklist. Create this month's audit to begin the compliance record."
                        })
                    }),
                    audits.length > 0 ? /*#__PURE__*/ _jsxs("p", {
                        className: "px-1 text-[11.5px] text-muted-foreground",
                        children: [
                            audits.length,
                            " ",
                            audits.length === 1 ? 'audit' : 'audits',
                            " · scores freeze when an audit is published · final audits are exempt from the KPI"
                        ]
                    }) : null
                ]
            })
        ]
    });
}
